import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Skeleton, Typography } from 'wtb-components';
import {
  Block,
  ContinueToPaymentButton,
  Payment,
  PersonalInfoForm,
  PurchaseInfo
} from 'components';
import {
  useAppContext,
  useTransactionContext,
  useLocaleContext,
  useBillingContext
} from 'context';
import { APP_STEPS, ANALYTICS_EVENTS } from 'constants.js';
import { useSendAnalyticsEvent } from 'hooks';
import EmailValidationSnackbar from 'components/EmailValidationSnackbar';
import styles from './Checkout.module.css';

const Title = () => {

  const { isLoading } = useAppContext();
  const { translate } = useLocaleContext();

  return isLoading ? (
    <div className={styles.loadingTitle}>
      <Skeleton height={40} width="50%" />
    </div>
  ) : (
    <Typography className={styles.title} component="div" variant="h6">
      {translate('checkout.title')}
    </Typography>
  );
};

const ALLOWED_STEPS = Object.values(APP_STEPS);

const Checkout = () => {
  const { step } = useParams();

  const {
    furtherStep,
    setAppStep
  } = useAppContext();
  const { translate } = useLocaleContext();
  const {
    hasSentDetails,
    hasPersonalInfo,
    formRequiredFields
  } = useTransactionContext();
  const { billing, setBilling } = useBillingContext();

  const [ eventSent, setEventSent ] = useState(false);

  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const isOrderSummaryStep = step === APP_STEPS.ORDER_SUMMARY;
  const isCustomerInfoStep = step === APP_STEPS.PERSONAL_INFORMATION;
  const isPaymentStep = step === APP_STEPS.PAYMENT;

  useEffect(() => {
    if (!step || !ALLOWED_STEPS.includes(step)) {
      setAppStep(APP_STEPS.ORDER_SUMMARY);
    }
  }, [ step, setAppStep ]);

  useEffect(() => {
    if (step && !furtherStep) {
      setAppStep(step);
    }
  }, [ step, furtherStep, setAppStep ]);

  useEffect(() => {
    if (!sendAnalyticsEvent || eventSent) return;

    setEventSent(true);

    sendAnalyticsEvent(ANALYTICS_EVENTS.LOAD);
  }, [ sendAnalyticsEvent, eventSent ]);

  return (
    <div className={styles.root}>
      <Title />

      <Block
        main
        id="order-summary-block"
        position={1}
        checked={furtherStep > 1}
        collapsable={false}
        expanded={isOrderSummaryStep}
        hasAction={furtherStep > 1 && !isOrderSummaryStep}
        actionHandler={() => setAppStep(APP_STEPS.ORDER_SUMMARY)}
        actionText={translate('sections.purchaseInfoView')}
        title={translate('sections.purchaseInfo')}
        titleProps={{ id: 'view-order' }}
      >
        <PurchaseInfo />
      </Block>

      <Block
        main
        position={2}
        id="personal-information-block"
        disabled={furtherStep < 2 && !hasPersonalInfo()}
        checked={furtherStep > 2}
        collapsable={false}
        expanded={isCustomerInfoStep}
        paddedBody
        hasAction={(furtherStep >= 2 || hasPersonalInfo()) && !isCustomerInfoStep}
        actionHandler={() => setAppStep(APP_STEPS.PERSONAL_INFORMATION)}
        actionText={translate('common.edit')}
        title={translate('sections.customerData')}
        titleProps={{ id: 'edit-customer' }}
      >
        <PersonalInfoForm
          info={billing}
          setData={setBilling}
          fields={formRequiredFields}
          SubmitButton={ContinueToPaymentButton}
        />
      </Block>

      <Block
        main
        position={3}
        checked={furtherStep > 3}
        disabled={!hasSentDetails}
        collapsable={false}
        expanded={isPaymentStep}
        hasAction={hasSentDetails && !isPaymentStep}
        actionHandler={() => setAppStep(APP_STEPS.PAYMENT)}
        title={translate('sections.payment')}
        titleProps={{ id: 'edit-payment' }}
        actionText={translate('sections.paymentEdit')}
      >
        <Payment />
      </Block>

      <EmailValidationSnackbar />
    </div>
  );
};

export default Checkout;
